const env = (import.meta as ImportMeta & { env?: Record<string, string | undefined> }).env ?? {};

const DEFAULT_AURORA_API_URL = '';

export const AURORA_API_URL = (env.VITE_AURORA_API_URL || DEFAULT_AURORA_API_URL).trim();

export type AuroraEndpointKey =
  | 'chat'
  | 'consultar-nr'
  | 'analisar-risco'
  | 'gerar-documento'
  | 'gerar-treinamento'
  | 'gerar-diagrama'
  | 'gerar-apr'
  | 'esocial'
  | 'investigar-acidente';

export type AuroraEndpointConfig = {
  key: AuroraEndpointKey;
  path: string;
  method: 'POST';
  label: string;
  samplePayload: Record<string, unknown>;
};

export const AURORA_ENDPOINTS: AuroraEndpointConfig[] = [
  {
    key: 'chat',
    path: '/api/chat',
    method: 'POST',
    label: 'Chat Operacional',
    samplePayload: { message: 'Olá Aurora, execute um diagnóstico rápido de SST.' },
  },
  {
    key: 'consultar-nr',
    path: '/api/consultar-nr',
    method: 'POST',
    label: 'Consultar NR',
    samplePayload: { nr: 'NR-12' },
  },
  {
    key: 'analisar-risco',
    path: '/api/analisar-risco',
    method: 'POST',
    label: 'Análise de Risco',
    samplePayload: { problema: 'Queda em altura durante manutenção', metodo: 'ishikawa' },
  },
  {
    key: 'gerar-documento',
    path: '/api/gerar-documento',
    method: 'POST',
    label: 'Gerar Documento',
    samplePayload: { tipo: 'PGR', empresa: 'Empresa Exemplo', setor: 'Operação' },
  },
  {
    key: 'gerar-treinamento',
    path: '/api/gerar-treinamento',
    method: 'POST',
    label: 'Gerar Treinamento',
    samplePayload: { tema: 'Uso correto de EPI', publico: 'operadores', slides: 10 },
  },
  {
    key: 'gerar-diagrama',
    path: '/api/gerar-diagrama',
    method: 'POST',
    label: 'Gerar Diagrama',
    samplePayload: { problema: 'Acidente com empilhadeira' },
  },
  {
    key: 'gerar-apr',
    path: '/api/gerar-apr',
    method: 'POST',
    label: 'Gerar APR',
    samplePayload: { atividade: 'Solda em altura', local: 'Galpão 3' },
  },
  {
    key: 'esocial',
    path: '/api/esocial',
    method: 'POST',
    label: 'eSocial',
    samplePayload: { evento: 'S-2210', acao: 'orientar', dados: 'Acidente sem afastamento' },
  },
  {
    key: 'investigar-acidente',
    path: '/api/investigar-acidente',
    method: 'POST',
    label: 'Investigar Acidente',
    samplePayload: { acidente: 'Prensagem de mão na prensa hidráulica', setor: 'Metalurgia' },
  },
];

export type AuroraRequestOptions = {
  timeoutMs?: number;
  signal?: AbortSignal;
  maxRetries?: number;
  onTelemetry?: (event: AuroraTelemetryEvent) => void;
};

export type AuroraTelemetryEvent = {
  phase: 'start' | 'retry' | 'success' | 'error';
  endpointKey: AuroraEndpointKey;
  endpointPath: string;
  attempt: number;
  elapsedMs: number;
  status?: number;
  detail?: string;
};

export type AuroraHealthSnapshot = {
  online: boolean;
  latencyMs: number;
  statusCode: number;
  health: unknown;
  status: unknown;
  error?: string;
};

export class AuroraApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly detail: string,
    public readonly elapsedMs: number,
  ) {
    super(`Aurora API ${status}: ${detail}`);
    this.name = 'AuroraApiError';
  }
}

const MAX_RETRIES = 2;
const RETRY_DELAY_MS = 800;

function isRetryable(status: number): boolean {
  return status === 429 || status === 502 || status === 503 || status === 504;
}

function normalizeBaseUrl(baseUrl: string): string {
  return baseUrl.replace(/\/+$/, '');
}

function buildEndpointUrl(path: string): string {
  const baseUrl = normalizeBaseUrl(AURORA_API_URL);
  if (!baseUrl) {
    throw new AuroraApiError(
      0,
      'AURORA_API_URL não configurada para este ambiente. Defina VITE_AURORA_API_URL no deploy.',
      0,
    );
  }
  return `${baseUrl}${path}`;
}

export async function fetchAuroraHealth(timeoutMs = 12000): Promise<AuroraHealthSnapshot> {
  const startedAt = performance.now();
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), timeoutMs);

  try {
    const healthUrl = buildEndpointUrl('/health');
    const statusUrl = buildEndpointUrl('/api/status');

    const [healthRes, statusRes] = await Promise.all([
      fetch(healthUrl, { method: 'GET', signal: controller.signal, cache: 'no-store' }),
      fetch(statusUrl, { method: 'GET', signal: controller.signal, cache: 'no-store' }),
    ]);

    const latencyMs = Math.round(performance.now() - startedAt);
    const healthData = await healthRes.json().catch(() => null);
    const statusData = await statusRes.json().catch(() => null);
    const ok = healthRes.ok && statusRes.ok;

    return {
      online: ok,
      latencyMs,
      statusCode: ok ? 200 : Math.max(healthRes.status, statusRes.status),
      health: healthData,
      status: statusData,
    };
  } catch (err) {
    return {
      online: false,
      latencyMs: Math.round(performance.now() - startedAt),
      statusCode: 0,
      health: null,
      status: null,
      error: err instanceof Error ? err.message : String(err),
    };
  } finally {
    window.clearTimeout(timeoutId);
  }
}

export async function callAuroraEndpoint(
  endpoint: AuroraEndpointConfig,
  payload: Record<string, unknown>,
  options: AuroraRequestOptions = {},
): Promise<{ status: number; data: unknown; elapsedMs: number }> {
  const timeoutMs = options.timeoutMs ?? 45000;
  const maxRetries = options.maxRetries ?? MAX_RETRIES;
  const startedAt = performance.now();
  const targetUrl = buildEndpointUrl(endpoint.path);

  options.onTelemetry?.({
    phase: 'start',
    endpointKey: endpoint.key,
    endpointPath: endpoint.path,
    attempt: 0,
    elapsedMs: 0,
  });

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), timeoutMs);

    if (options.signal) {
      options.signal.addEventListener('abort', () => controller.abort(), { once: true });
    }

    // Abortar imediatamente se o sinal externo já foi disparado
    if (options.signal?.aborted) {
      window.clearTimeout(timeoutId);
      options.onTelemetry?.({
        phase: 'error',
        endpointKey: endpoint.key,
        endpointPath: endpoint.path,
        attempt,
        elapsedMs: Math.round(performance.now() - startedAt),
        status: 0,
        detail: 'cancelled-by-user',
      });
      throw new AuroraApiError(0, 'Requisição cancelada pelo usuário.', Math.round(performance.now() - startedAt));
    }

    try {
      const response = await fetch(targetUrl, {
        method: endpoint.method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        signal: controller.signal,
      });

      const elapsedMs = Math.round(performance.now() - startedAt);
      const text = await response.text();
      let data: unknown = text;
      try {
        data = text ? JSON.parse(text) : null;
      } catch {
        data = text;
      }

      // Retry em erros transitórios
      if (!response.ok && isRetryable(response.status) && attempt < maxRetries) {
        options.onTelemetry?.({
          phase: 'retry',
          endpointKey: endpoint.key,
          endpointPath: endpoint.path,
          attempt,
          elapsedMs,
          status: response.status,
          detail: 'retryable-http-status',
        });
        await new Promise<void>((resolve) => window.setTimeout(resolve, RETRY_DELAY_MS * (attempt + 1)));
        continue;
      }

      options.onTelemetry?.({
        phase: response.ok ? 'success' : 'error',
        endpointKey: endpoint.key,
        endpointPath: endpoint.path,
        attempt,
        elapsedMs,
        status: response.status,
        detail: response.ok ? 'ok' : 'http-error',
      });

      return { status: response.status, data, elapsedMs };
    } catch (err) {
      const elapsedMs = Math.round(performance.now() - startedAt);
      const isAbort = err instanceof DOMException && err.name === 'AbortError';

      if (isAbort) {
        options.onTelemetry?.({
          phase: 'error',
          endpointKey: endpoint.key,
          endpointPath: endpoint.path,
          attempt,
          elapsedMs,
          status: 0,
          detail: 'timeout',
        });
        throw new AuroraApiError(0, 'Tempo limite excedido. Verifique a conexão com o servidor Aurora.', elapsedMs);
      }

      // Retry em falha de rede (não abort)
      if (attempt < maxRetries) {
        options.onTelemetry?.({
          phase: 'retry',
          endpointKey: endpoint.key,
          endpointPath: endpoint.path,
          attempt,
          elapsedMs,
          status: 0,
          detail: 'network-retry',
        });
        await new Promise<void>((resolve) => window.setTimeout(resolve, RETRY_DELAY_MS * (attempt + 1)));
        continue;
      }

      const message = err instanceof Error ? err.message : String(err);
      options.onTelemetry?.({
        phase: 'error',
        endpointKey: endpoint.key,
        endpointPath: endpoint.path,
        attempt,
        elapsedMs,
        status: 0,
        detail: message,
      });
      throw new AuroraApiError(0, `Erro de rede: ${message}`, elapsedMs);
    } finally {
      window.clearTimeout(timeoutId);
    }
  }

  // Fallback — nunca deve chegar aqui
  throw new AuroraApiError(0, 'Falha após múltiplas tentativas.', Math.round(performance.now() - startedAt));
}
